import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UploadSimple, Paperclip, X, FileText, Image as ImgIcon } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";

const MAX_BYTES = 3 * 1024 * 1024; // 3MB safe for base64 payload

export default function HomeworkSubmitDialog({ homework, studentId, open, onOpenChange, onSubmitted }) {
  const [files, setFiles] = useState([]); // [{name,type,size,data}]
  const [note, setNote] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const readFile = (file) => new Promise((resolve, reject) => {
    if (file.size > MAX_BYTES) return reject(new Error(`"${file.name}" exceeds 3MB limit`));
    const reader = new FileReader();
    reader.onload = () => resolve({ name: file.name, type: file.type, size: file.size, data: reader.result });
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const onPick = async (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of picked) {
        try {
          const local = await readFile(f);
          const { data } = await api.post("/uploads", local);
          uploaded.push({ ...data });
        } catch (err) { toast.error(err.message || formatApiError(err)); }
      }
      setFiles((prev) => [...prev, ...uploaded]);
    } finally { setUploading(false); e.target.value = ""; }
  };

  const remove = (idx) => setFiles((f) => f.filter((_, i) => i !== idx));

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/homework/${homework.id}/submit`, { student_id: studentId, note, files });
      toast.success("Homework submitted");
      onSubmitted?.();
      onOpenChange(false);
      setFiles([]); setNote("");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Manrope" }}>Submit — {homework?.title}</DialogTitle>
          <div className="text-xs text-muted-foreground">Due {homework?.due_date}</div>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">Attachments</Label>
            <label htmlFor="hw-file-input" data-testid="hw-upload-input-label"
              className={cn("mt-1.5 flex flex-col items-center justify-center gap-2 border-2 border-dashed border-border rounded-md py-8 px-4 cursor-pointer hover:border-foreground/30 transition-colors",
                uploading && "opacity-60 pointer-events-none")}>
              <UploadSimple size={22} weight="duotone" className="text-primary" />
              <div className="text-sm font-semibold">{uploading ? "Uploading..." : "Click to upload photos or PDF"}</div>
              <div className="text-[11px] text-muted-foreground">Max 3MB per file · JPG, PNG, PDF</div>
              <input id="hw-file-input" data-testid="hw-file-input" type="file"
                multiple accept="image/*,application/pdf,.doc,.docx,.txt"
                onChange={onPick} className="hidden" />
            </label>
          </div>

          {files.length > 0 && (
            <ul data-testid="hw-file-list" className="space-y-2">
              {files.map((f, i) => (
                <li key={i} className="flex items-center gap-2 px-3 py-2 rounded-md border border-border/60 bg-secondary/40">
                  {f.type?.startsWith("image/") ? <ImgIcon size={16} weight="duotone" /> : <FileText size={16} weight="duotone" />}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate">{f.name}</div>
                    <div className="text-[10px] text-muted-foreground">{Math.round((f.size || 0) / 1024)} KB</div>
                  </div>
                  <button onClick={() => remove(i)} className="text-muted-foreground hover:text-destructive">
                    <X size={14} />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">Note (optional)</Label>
            <Textarea data-testid="hw-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="mt-1.5" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving || uploading || files.length === 0}
            data-testid="hw-submit-btn" className="gap-2">
            <Paperclip size={14} /> {saving ? "Submitting..." : "Submit homework"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
